// @flow
// Child nodes in jsx are typed as any, so we have to suppress weak types
import React, { Children, isValidElement, cloneElement } from 'react';
import { renderToString } from 'react-dom/server';

/**
 * Wraps around a single child and renders its children to a react string
 */
const StaticContents = ({children}: {children: any}): React.Element<*> => { // eslint-disable-line flowtype/no-weak-types
	const child = Children.only(children);
	if (!isValidElement(child)) {
		throw TypeError(`only child was ${typeof child}, not a react element.`);
	}
	const innerHtml = Children.map(child.props.children || [], (child) => {
		if (child.type && child.props) {
			return renderToString(child);
		} else if (child instanceof Function) {
			return child();
		} else {
			return child;
		}
	}).join('');
	return cloneElement(child, Object.assign({
		dangerouslySetInnerHTML: {
			__html: innerHtml
		}
	}), null);
};

/**
 * A script tag with innerhtml set to the script
 * @param string content     the script to include
 * @returns React.Element
 */
const ServerScript = ({content}: {content: string}): React.Element<*> => {
	return (
		<script type='text/javascript' dangerouslySetInnerHTML={{
			__html: content
		}} />
	);
};
/**
 * An Html comment -- stripped out in the conversion of jsx to html
 * @param string content
 * @return React.Element
 */
const HtmlComment = ({content}: {content: string}): React.Element<*> => {
	return (
		<react-comment dangerouslySetInnerHTML={{
			__html: `<!-- ${content} -->`
		}} />
	);
};

/**
 * Standard <head> meta that is on every interactive.
 */
const headerCruft = ({description}: {description: string} = {description: 'Pop culture obsessives writing for the pop culture obsessed'}) => [
	<meta charSet='UTF-8' />,
	<meta name='viewport' content='width=device-width, initial-scale=1.0' />,
	<meta httpEquiv='X-UA-Compatible' content='IE=edge,chrome=1' />,
	<meta name='description' content={description} />,
	<meta name='keywords' content />,
	<meta property='fb:app_id' content={130164583741660} />,
	<meta property='fb:pages' content={39581755672} />
];

type OpenGraphImage = {url: string, type: string, width: number, height: number}

/**
 * OpenGraph meta tags
 */
const openGraph = ({title, type, url, description, image}: {title: string, type: string, url: string, description: string, image: OpenGraphImage}) => [
	<meta property='og:title' content={title} />,
	<meta property='og:type' content={type} />,
	<meta property='og:url' content={url} />,
	<meta property='og:description' content={description} />,
	<meta property='og:image' content={image.url} />,
	<meta property='og:image:type' content={image.type} />,
	<meta property='og:image:width' content={image.width} />,
	<meta property='og:image:height' content={image.height} />
];

/**
 * Twitter meta tags
 */
const twitterMeta = ({site, card, image, url, title, description}: {
	site: string,
	card: string,
	image: string,
	url: string,
	title: string,
	description: string
}) => [
	<meta name='twitter:site' content={site} />,
	<meta name='twitter:card' content={card} />,
	<meta property='twitter:image' content={image} />,
	<meta name='twitter:url' content={url} />,
	<meta name='twitter:title' content={title} />,
	<meta name='twitter:description' content={description} />
];

/**
 * Twitter meta tags
 */
const dfpScripts = (section: string) => [
	<ServerScript content={`
		window.TARGETING = {
			'dfp_site': 'newavclub',
		};
	`} />,
	<ServerScript content={`
		TARGETING.dfp_section = "${section}";
	`} />
];

export {
	StaticContents,
	ServerScript,
	HtmlComment,
	headerCruft,
	openGraph,
	twitterMeta,
	dfpScripts
};



// WEBPACK FOOTER //
// ./common/components/ServerSideComponents.jsx